import { createFileRoute } from "@tanstack/react-router";
import { PortableText } from "@portabletext/react";
import { getLandingPage } from "~/lib/server-fns";
import { sanityImageUrl } from "~/lib/sanity-image";
import { portableTextComponents } from "~/components/portable-text-components";
import { captureHandledException } from "~/lib/handled-errors";

const fallback = {
  title: "CORE Snapshot",
  timeEstimate: "Takes about 5 minutes",
  ctaText: "Start Your Assessment",
};

export const Route = createFileRoute("/")({
  component: Home,
  validateSearch: (search: Record<string, unknown>) => ({
    preview: (search.preview === "true" || search.preview === true) || undefined,
  }),
  loaderDeps: ({ search }) => ({ preview: !!search.preview }),
  loader: async ({ deps }) => {
    try {
      const page = await getLandingPage({ data: { preview: deps.preview } });
      return page || null;
    } catch (err) {
      await captureHandledException(err, {
        feature: "landing",
        action: "load-page",
        layer: "loader",
        fallback: "fallback-copy",
        extra: { preview: deps.preview },
      });
      return null;
    }
  },
});

function Home() {
  const page = Route.useLoaderData();

  const title = page?.title || fallback.title;
  const timeEstimate = page?.timeEstimate || fallback.timeEstimate;
  const ctaText = page?.ctaText || fallback.ctaText;
  const ctaLink = "/demographics";
  const heroImage = page?.heroImage;
  const heroSrc = sanityImageUrl(heroImage, { width: 1600, quality: 80 });

  return (
    <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl">
        {heroSrc && (
          <img
            src={heroSrc}
            alt={heroImage?.alt ?? ""}
            className="w-full h-auto rounded-lg mb-8 aspect-[16/9] object-cover"
            loading="eager"
          />
        )}
        <h1 className="text-5xl text-core-orange mb-6 text-center">{title}</h1>

        <div className="space-y-5 text-white/80 leading-relaxed mb-8">
          {page?.body ? (
            <PortableText
              value={page.body}
              components={portableTextComponents}
            />
          ) : (
            <>
              <p>
                The <strong>SNAPSHOT</strong> is a tool for self-reflection. It
                helps you see how well you understand your{" "}
                <strong>IDENTITY</strong> in Scripture, how connected you feel
                to your <strong>COMMUNITY</strong>, and how clearly you&rsquo;re
                living out your <strong>PURPOSE</strong>.
              </p>
              <p>
                We&rsquo;re not asking you to &lsquo;take an assessment&rsquo;.
                Think of it more like looking in a mirror. And
                yes&hellip;mirrors can be uncomfortable. But here&rsquo;s the
                deal: you can&rsquo;t change what you can&rsquo;t see.
              </p>
              <p>
                Getting honest about your strengths and recognizing your biggest
                growth opportunities is a game-changer. Those of us who avoid
                looking inward often drift off course. The{" "}
                <strong>SNAPSHOT</strong> gives you a clear picture of where you
                are today&mdash;so you can start moving in the right direction.
              </p>
            </>
          )}
        </div>

        <p className="text-core-body mb-8 text-center">{timeEstimate}</p>

        <div className="text-center">
          <a
            href={ctaLink}
            className="inline-block bg-core-orange text-white font-bold py-3 px-10 rounded-full text-lg hover:opacity-90 transition-opacity"
          >
            {ctaText}
          </a>
        </div>
      </div>
    </div>
  );
}
